import { differenceInCalendarDays } from 'date-fns';
import { formatCurrency } from '@/Utils/FnUtils';
import {
  combineDateAndTime,
  MISSION_DESCRIPTION_DEFAULT,
  MISSION_TITLE_DEFAULT,
} from '../constants';
import type { CreateMissionFormType, MissionRewardLevel } from '../types';
import { buildRewardLevels } from './map-create-mission-form-to-payload';

export type MissionPreviewRewardLevel = MissionRewardLevel & {
  position: number;
  rewardLabel: string;
};

export type MissionPreviewData = {
  title: string;
  description: string;
  missionName: string;
  maxValueLabel: string;
  suggestedAverageLabel: string;
  rewardsSectionTitle: string;
  rewardLevels: MissionPreviewRewardLevel[];
};

const EMPTY_MAX_VALUE_LABEL = '— entregas';
const EMPTY_SUGGESTED_AVERAGE_LABEL = '— entregas por dia';
const EMPTY_MISSION_NAME = 'Nome da missão';

type MissionPreviewFormFields = Pick<
  CreateMissionFormType,
  | 'missionName'
  | 'missionTitle'
  | 'missionDescription'
  | 'objective01'
  | 'objective02'
  | 'objective03'
  | 'reward01'
  | 'reward02'
  | 'reward03'
  | 'startDate'
  | 'startTime'
  | 'endDate'
  | 'endTime'
>;

const buildSuggestedAverageLabel = (
  maxDeliveries: number,
  startDate?: Date,
  startTime?: Date,
  endDate?: Date,
  endTime?: Date,
) => {
  if (!maxDeliveries || !startDate || !startTime || !endDate || !endTime) {
    return EMPTY_SUGGESTED_AVERAGE_LABEL;
  }

  const start = combineDateAndTime(startDate, startTime);
  const end = combineDateAndTime(endDate, endTime);
  const days = differenceInCalendarDays(end, start) + 1;

  if (days <= 0) {
    return EMPTY_SUGGESTED_AVERAGE_LABEL;
  }

  const average = Math.ceil(maxDeliveries / days);
  return `${average} ${average === 1 ? 'entrega' : 'entregas'} por dia`;
};

export const buildMissionPreviewData = (
  data: MissionPreviewFormFields,
): MissionPreviewData => {
  const title = data.missionTitle?.trim() || MISSION_TITLE_DEFAULT;
  const description =
    data.missionDescription?.trim() || MISSION_DESCRIPTION_DEFAULT;
  const missionName = data.missionName?.trim() || EMPTY_MISSION_NAME;

  const rewardLevels = buildRewardLevels(data)
    .map((level, index) => ({
      ...level,
      position: index + 1,
      rewardLabel: formatCurrency(level.rewardValue),
    }))
    .sort((a, b) => b.position - a.position);

  const maxConditionValue = rewardLevels.length
    ? Math.max(...rewardLevels.map(level => level.conditionValue))
    : 0;

  return {
    title,
    description,
    missionName,
    maxValueLabel: maxConditionValue
      ? `${maxConditionValue} entregas`
      : EMPTY_MAX_VALUE_LABEL,
    suggestedAverageLabel: buildSuggestedAverageLabel(
      maxConditionValue,
      data.startDate,
      data.startTime,
      data.endDate,
      data.endTime,
    ),
    rewardsSectionTitle: title,
    rewardLevels,
  };
};
